

import React, { useRef, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useCubeTexture, useIntersect } from "@react-three/drei"
import * as THREE from 'three'
import { useStore } from './store'
import { useLocation } from "react-router-dom"

function Polygon(props) {
    const dragging = useRef(useStore.getState().dragging)
    const hover = useStore.getState().hover
    const unhover = useStore.getState().unhover
    const transition = useStore(state => state.transition)

    useEffect(() => useStore.subscribe(
      state => (dragging.current = state.dragging)
    ), [])
    const visible = useRef(false)
    const mesh = useIntersect((isVisible) => (visible.current = isVisible))
    const mousePos = useRef({x: 0, y : 0})

    const onMouseMove = event => {
      mousePos.current = {x: event.pageX / window.innerWidth, y: event.pageY / window.innerHeight}
    }

    useEffect(() => {
      window.addEventListener('mousemove', onMouseMove)
      return () => {
        window.removeEventListener('mousemove', onMouseMove)
      }
    }, [])

    const scrollPosition = useRef({y: 0})
    const handleScroll = (e) => {
      scrollPosition.current = {y: e.target.scrollTop * 0.025}
    };

    useEffect(() => {
        document.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useFrame((state, delta) => {
      const p = state.camera.position
      const s = THREE.MathUtils.lerp(mesh.current.scale.x, transition ? 0 : 1, 0.05)
      mesh.current.scale.set(s, s, s)
      mesh.current.material.opacity = s
      if(dragging.current) {
        mesh.current.rotation.y = THREE.MathUtils.damp(mesh.current.rotation.y, (mousePos.current.x*2-1)*3, 1, delta)
        mesh.current.rotation.z =  THREE.MathUtils.damp(mesh.current.rotation.z, (mousePos.current.y*2-1)*3, 1, delta)
        
        mesh.current.position.x = THREE.MathUtils.damp(mesh.current.position.x, THREE.MathUtils.lerp(-p.z, p.z, mousePos.current.x), 1, delta)
        mesh.current.position.y = THREE.MathUtils.damp(mesh.current.position.y, THREE.MathUtils.lerp(p.y+scrollPosition.current.y-2, p.y+scrollPosition.current.y+2, 1-mousePos.current.y), 0.8, delta)
        mesh.current.position.z = THREE.MathUtils.damp(mesh.current.position.z, THREE.MathUtils.lerp(p.x, -p.x, mousePos.current.x), 1, delta)
      } else {
        mesh.current.rotation.y += 0.005
        if(mesh.current.rotation.y > 2 * Math.PI) mesh.current.rotation.y -= 2 * Math.PI
        mesh.current.rotation.z += 0.005
        if(mesh.current.rotation.z > 2 * Math.PI) mesh.current.rotation.z -= 2 * Math.PI
        mesh.current.position.x = THREE.MathUtils.damp(mesh.current.position.x, 0, 1, delta)
        mesh.current.position.y = THREE.MathUtils.damp(mesh.current.position.y, scrollPosition.current.y + Math.sin(p.x*0.8) * 0.25, 4, delta)
        mesh.current.position.z = THREE.MathUtils.damp(mesh.current.position.z, 0, 1, delta)
      }
    })
  
    const materialProps = {
      side: THREE.DoubleSide,
      color: props.color,
      envMap: props.envMap,
      blending: THREE.AdditiveBlending,
      transparent: true,
      refractionRatio: props.refractionRatio
    }
  
    return (
      <group
        position={props.position}
      >
        <mesh
          geometry={props.geometry}
          scale={[0, 0, 0]}
          ref={mesh}
          onPointerEnter={() => hover(-1, -1, 200)}
          onPointerLeave={() => unhover()}
        >
          <meshBasicMaterial
            {...materialProps}
          />
        </mesh>
      </group>
    )
  }
  
  function ChromaticAberrationPolygon(props) {
    return (
      <group>
        <Polygon {...props} color="#aaddff" refractionRatio={0.9} />
        <Polygon {...props} color="#88ff00" refractionRatio={0.9-0.025} />
        <Polygon {...props} color="#ff0055" refractionRatio={0.9-0.05} />
      </group>
    )
}

export function Cube(props) {
  const envMap = useCubeTexture(['px.jpg','nx.jpg','py.jpg','ny.jpg','pz.jpg','nz.jpg'], { path: 'cube/' })
  envMap.mapping = THREE.CubeRefractionMapping
  const location = useLocation()
  const { width, height } = useThree(state => state.viewport)
  const scale = Math.max(width, height) * 0.1
  return (
    <>
        { location.pathname === '/about' ? 
          <ChromaticAberrationPolygon envMap={envMap} position={[0, 0, 0]} geometry={new THREE.IcosahedronBufferGeometry(scale*0.9)}/> :
          location.pathname === '/' ? <ChromaticAberrationPolygon envMap={envMap} position={[0, 0, 0]} geometry={new THREE.BoxBufferGeometry(scale, scale, scale)}/> : <></>
        }
    </>
  )
}